import { useTranslation } from 'react-i18next';

import { Icon } from '@sdb/planning-ui';

import { Button, MenuItem, Tooltip } from '@mui/material';
import {
  ColumnsPanelTrigger,
  ExportCsv,
  Toolbar,
  ToolbarButton
} from '@mui/x-data-grid-pro';
import {
  ToolbarActions,
  ToolbarRow,
  YearSelect
} from '../../EmploymentHours.styles';

import type { EmploymentHoursToolbarProps } from './EmploymentHoursToolbar.types';

/**
 * Replaces the three controls in `EmployeeWorkAgreementsHours.html` (year select, Recalculate,
 * Year corrections) and adds the column picker and CSV export.
 *
 * The picker is MUI's own columns panel — with grouped headers it lists all ~85 columns by group, so
 * no custom UI is needed to replace Dojo's click-to-expand header links.
 */
const EmploymentHoursToolbar = ({
  years,
  selectedYear,
  onYearChange,
  showYearSelector,
  canRecalculateHours,
  canViewYearCorrections,
  isRecalculating,
  onRecalculate,
  onOpenYearCorrections
}: EmploymentHoursToolbarProps) => {
  'use memo';

  const { t } = useTranslation('common', { keyPrefix: 'employmentHours' });

  return (
    <Toolbar>
      <ToolbarRow>
        {/* no floating label: the years speak for themselves in a toolbar */}
        {showYearSelector && (
          <YearSelect
            size="small"
            value={selectedYear ?? ''}
            onChange={(event) => onYearChange(Number(event.target.value))}
            // label-less, so the accessible name has to come from the select itself
            inputProps={{ 'aria-label': t('year') }}
          >
            {years.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </YearSelect>
        )}

        <ToolbarActions>
          {canRecalculateHours && (
            <Button
              variant="outlined"
              size="small"
              loading={isRecalculating}
              disabled={!selectedYear}
              onClick={onRecalculate}
            >
              {t('recalculate')}
            </Button>
          )}

          {canViewYearCorrections && (
            <Button
              variant="outlined"
              size="small"
              disabled={!selectedYear}
              onClick={onOpenYearCorrections}
            >
              {t('yearCorrections')}
            </Button>
          )}

          <Tooltip title={t('selectColumns')}>
            <ColumnsPanelTrigger
              render={<ToolbarButton aria-label={t('selectColumns')} />}
            >
              <Icon type="table-columns" fontSize="small" />
            </ColumnsPanelTrigger>
          </Tooltip>

          <Tooltip title={t('exportCsv')}>
            <ExportCsv render={<ToolbarButton aria-label={t('exportCsv')} />}>
              <Icon type="file-csv" fontSize="small" />
            </ExportCsv>
          </Tooltip>
        </ToolbarActions>
      </ToolbarRow>
    </Toolbar>
  );
};

export default EmploymentHoursToolbar;
